import { local, StorageKey } from "../lib/storage"

/** Роли из Product.Common/Role.cs — значения в JWT приходят строками. */
export const Roles = {
	Admin: "Admin",
	ContentManagerRF: "ContentManagerRF",
	ContentManagerLv: "ContentManagerLv",
	Translator: "Translator",
	Photographer: "Photographer",
	ContentManagerRg: "ContentManagerRg",
	ContentManagerCn: "ContentManagerCn",
	SupplyManager: "Supply_manager",
} as const

export type Role = (typeof Roles)[keyof typeof Roles]

/** Роли, которым рижский интерфейс вообще предназначен. */
export const RIGA_ROLES: Role[] = [
	Roles.ContentManagerRg,
	Roles.ContentManagerLv,
	Roles.Translator,
	Roles.Admin,
]

const ROLE_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
const NAME_CLAIM = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"
const SURNAME_CLAIM =
	"http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname"

export interface Session {
	token: string
	username: string
	displayName: string
	roles: Role[]
	expiresAt: number
}

export function getToken(): string | null {
	return local.text(StorageKey.token)
}

export function setToken(token: string): void {
	local.write(StorageKey.token, token)
}

export function clearSession(): void {
	local.remove(StorageKey.token)
}

/** SHA256(password) в hex — ровно как utils/passwordHasher.ts старого фронта.
 *  В БД лежит клиентский хеш, поэтому иначе логин не пройдёт. */
export async function hashPassword(password: string): Promise<string> {
	const bytes = new TextEncoder().encode(password)
	const digest = await crypto.subtle.digest("SHA-256", bytes)
	return Array.from(new Uint8Array(digest))
		.map(byte => byte.toString(16).padStart(2, "0"))
		.join("")
}

function decodePayload(token: string): Record<string, unknown> | null {
	const part = token.split(".")[1]
	if (!part) return null
	try {
		const base64 = part.replace(/-/g, "+").replace(/_/g, "/")
		const json = decodeURIComponent(
			atob(base64)
				.split("")
				.map(char => "%" + char.charCodeAt(0).toString(16).padStart(2, "0"))
				.join("")
		)
		return JSON.parse(json) as Record<string, unknown>
	} catch {
		return null
	}
}

export function parseSession(token: string): Session | null {
	const payload = decodePayload(token)
	if (!payload) return null

	const rawRoles = payload[ROLE_CLAIM]
	const roles = (
		Array.isArray(rawRoles) ? rawRoles : rawRoles ? [rawRoles] : []
	).map(String) as Role[]

	const expiresAt = Number(payload["exp"] ?? 0) * 1000
	if (!expiresAt || expiresAt < Date.now()) return null

	const name = String(payload[NAME_CLAIM] ?? payload["Username"] ?? "")
	const surname = String(payload[SURNAME_CLAIM] ?? "")

	return {
		token,
		username: String(payload["Username"] ?? name),
		displayName: [name, surname].filter(Boolean).join(" ") || name,
		roles,
		expiresAt,
	}
}

export function restoreSession(): Session | null {
	const token = getToken()
	if (!token) return null
	const session = parseSession(token)
	if (!session) {
		clearSession()
		return null
	}
	return session
}
